import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { mainActions } from "../../../store";
import classes from "./BoundaryDragChart.module.css";
import { boundaryTransferCount } from "../../code/BoundaryNoteMove";

export const boundaryTargets = (chartData, counts, string, note, omittedNotes = []) => {
  const noteId = counts.slice(0, string).reduce((sum, count) => sum + count, 0) + note;
  return [string - 1, string + 1].filter(target => target >= 0 && target < chartData.length &&
    boundaryTransferCount({ notesPerString: counts, omittedNotes }, string, target, noteId) > 0);
};

const BoundaryDragChart = ({ src, width, height, chartData, fullChartData, noteIds, frets, index, removeNotes, onFinishRemoving }) => {
  const dispatch = useDispatch();
  const chart = useSelector(state => state.refineData.perChartData[index]);
  const svg = useRef(null);
  const [drag, setDrag] = useState(null);
  const [message, setMessage] = useState("");
  const svgWidth = 40 + frets * 40;
  const svgHeight = 40 + (chartData.length - 1) * 20;
  const yFor = string => 20 + (chartData.length - 1 - string) * 20;
  // An undo, deletion or other edit cancels an unfinished gesture.
  useEffect(() => { setDrag(null); }, [chart, removeNotes]);
  useEffect(() => {
    if (!drag && !removeNotes) return;
    const cancel = event => { if (event.key === "Escape") { setDrag(null); onFinishRemoving(); setMessage("Editing cancelled."); } };
    document.addEventListener("keydown", cancel);
    return () => document.removeEventListener("keydown", cancel);
  }, [drag, removeNotes, onFinishRemoving]);

  const remove = noteId => {
    svg.current.focus({ preventScroll: true });
    dispatch(mainActions.omitRefineNote({ chartIndex: index, noteId }));
    setMessage("Note removed. Use this chart's Undo to restore it.");
  };

  const targetAt = event => {
    const rect = svg.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) * svgWidth / rect.width;
    const y = (event.clientY - rect.top) * svgHeight / rect.height;
    const target = chartData.length - 1 - Math.round((y - 20) / 20);
    return x >= 20 && x <= svgWidth - 20 && drag.targets.includes(target) && Math.abs(y - yFor(target)) <= 9
      ? target : null;
  };
  const drop = event => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!chart) { setDrag(null); return; }
    const target = targetAt(event);
    if (target !== null) {
      dispatch(mainActions.moveRefineBoundary({ chartIndex: index, from: drag.from, to: target, noteId: drag.noteId }));
      setMessage(`Note moved from string ${chart.notesPerString.length - drag.from} to string ${chart.notesPerString.length - target}. Pitch preserved.`);
    } else setMessage("Move cancelled. Drop on a highlighted adjacent string.");
    setDrag(null);
  };

  return <div className={classes.wrapper} style={{ width, height }}>
    <img src={src} alt="Guitar Chart data" width={width} height={height} draggable={false} />
    <svg ref={svg} tabIndex={-1} className={classes.overlay} viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      role="group" aria-label={`${removeNotes ? "Remove notes" : "Move boundary notes"} in chart ${index + 1}`}
      onPointerMove={event => {
        if (drag && event.pointerId === drag.pointerId) setDrag({ ...drag, over: targetAt(event) });
      }} onPointerUp={drop} onPointerCancel={() => setDrag(null)} onLostPointerCapture={() => setDrag(null)}>
      {drag && drag.targets.map(target => <rect key={target}
        className={drag.over === target ? classes.over : classes.target}
        x={20} y={yFor(target) - 9} width={svgWidth - 40} height={18} rx={4} />)}
      {chart && chartData.flatMap((notes, string) => notes.map(([fret], note) => {
        const noteId = noteIds[string][note];
        const originalNote = noteId - fullChartData.slice(0, string).reduce((sum, row) => sum + row.length, 0);
        const targets = boundaryTargets(fullChartData, chart.notesPerString, string, originalNote, chart.omittedNotes);
        if (!removeNotes && !targets.length) return null;
        return <circle key={`${string}-${note}`} cx={40 + fret * 40} cy={yFor(string)} r={10}
          data-tour-target={!removeNotes ? "refine-move" : undefined}
          className={removeNotes ? classes.removeHandle : classes.handle} role="button" tabIndex={0}
          aria-label={removeNotes ? `Remove note ${originalNote + 1} on string ${chart.notesPerString.length - string}`
            : `Move ${note === 0 ? "first" : "last"} note on string ${chart.notesPerString.length - string}`}
          onClick={() => { if (removeNotes) remove(noteId); }}
          onKeyDown={event => {
            if (removeNotes) {
              if (event.key === "Enter" || event.key === " ") { event.preventDefault(); remove(noteId); }
              return;
            }
            const target = event.key === "ArrowUp" ? string + 1 : event.key === "ArrowDown" ? string - 1 : null;
            if (targets.includes(target)) {
              event.preventDefault();
              dispatch(mainActions.moveRefineBoundary({ chartIndex: index, from: string, to: target, noteId }));
              setMessage(`Note moved to string ${chart.notesPerString.length - target}. Pitch preserved.`);
            }
          }}
          onPointerDown={event => {
            if (removeNotes) return;
            if (!event.isPrimary || event.button !== 0) return;
            event.preventDefault();
            event.currentTarget.focus();
            svg.current.setPointerCapture(event.pointerId);
            setDrag({ pointerId: event.pointerId, from: string, noteId, targets, over: null });
            setMessage("Drop on a highlighted adjacent string. Escape cancels.");
          }}><title>{removeNotes ? "Tap to remove this note. Undo restores it." : "Drag to a highlighted string, or use the up/down arrow keys."}</title></circle>;
      }))}
    </svg>
    <span className={classes.status} role="status">{message}</span>
  </div>;
};

export default BoundaryDragChart;
